import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Play, Trophy, Users, Calendar, DollarSign, X, Award, Square } from 'lucide-react';
import { formatPersianDate } from '../../utils/dateUtils';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import tournamentService from '../../services/tournamentService';
import { getFullImageUrl } from '../../utils/imageUpload';
import TournamentDetailModal from '../../components/Tournament/TournamentDetailModal';
import RecordResultModal from '../../components/Tournament/RecordResultModal';
import TournamentBracket from '../../components/Tournament/TournamentBracket';
import TournamentModal from '../../components/Modals/TournamentModal';
import FinishTournamentModal from '../../components/Tournament/FinishTournamentModal';
import toast from 'react-hot-toast';

const TournamentsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const TournamentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const TournamentCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const TournamentImage = styled.div`
  width: 100%;
  height: 180px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TournamentContent = styled.div`
  padding: 20px;
`;

const TournamentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const TournamentInfo = styled.div`
  flex: 1;
`;

const TournamentName = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px 0;
  line-height: 1.4;
`;

const TournamentId = styled.div`
  color: #6c6c8a;
  font-size: 12px;
  margin-bottom: 10px;
`;

const TournamentStatus = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'Running': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'Finished': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'Pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const TournamentDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #b8b8d1;
  font-size: 14px;
`;

const TournamentActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
`;

const BracketModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const BracketContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const BracketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const BracketTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const BracketBody = styled.div`
  padding: 25px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showTournamentModal, setShowTournamentModal] = useState(false);
  const [showAddWinnerModal, setShowAddWinnerModal] = useState(false);
  const [bracketData, setBracketData] = useState(null);
  const [bracketLoading, setBracketLoading] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await tournamentService.getTournaments();
      setTournaments(response || []);
    } catch (error) {
      toast.error('خطا در بارگذاری تورنومنت‌ها');
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBracket = async (tournamentId) => {
    try {
      setBracketLoading(true);
      const response = await tournamentService.getBracket(tournamentId);
      setBracketData(response);
    } catch (error) {
      toast.error('خطا در بارگذاری براکت تورنومنت');
      console.error('Error fetching bracket:', error);
    } finally {
      setBracketLoading(false);
    }
  };

  const filteredTournaments = tournaments.filter(tournament =>
    tournament.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tournament.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddTournament = () => {
    setSelectedTournament(null);
    setShowTournamentModal(true);
  };

  const handleEditTournament = (tournament) => {
    setSelectedTournament(tournament);
    setShowTournamentModal(true);
  };

  const handleTournamentModalClose = () => {
    setShowTournamentModal(false);
    setSelectedTournament(null);
  };

  const handleTournamentModalSuccess = () => {
    fetchTournaments();
  };

  const handleViewBracket = async (tournament) => {
    setSelectedTournament(tournament);
    setShowBracketModal(true);
    await fetchBracket(tournament.id);
  };

  const handleViewDetails = (tournament) => {
    setSelectedTournament(tournament);
    setShowDetailModal(true);
  };

  const handleStartTournament = async (tournament) => {
    if (window.confirm('آیا از شروع این تورنومنت اطمینان دارید؟ این کار ثبت‌نام‌ها را قفل می‌کند و مسابقات دور اول را تولید می‌کند.')) {
      try {
        await tournamentService.startTournament(tournament.id);
        toast.success('تورنومنت با موفقیت شروع شد');
        fetchTournaments();
      } catch (error) {
        toast.error('خطا در شروع تورنومنت');
        console.error('Error starting tournament:', error);
      }
    }
  };

  const handleAddWinner = async (tournament) => {
    setSelectedTournament(tournament);
    setShowAddWinnerModal(true);
  };

  const handleFinishTournament = async (tournamentId, winnerTeamId) => {
    try {
      await tournamentService.endTournament(tournamentId, winnerTeamId);
      toast.success('تورنومنت با موفقیت به پایان رسید');
      setShowAddWinnerModal(false);
      fetchTournaments();
    } catch (error) {
      toast.error('خطا در پایان دادن تورنومنت');
      console.error('Error finishing tournament:', error);
      throw error; // Re-throw to let the modal handle it
    }
  };

  const handleRecordResult = (match) => {
    setSelectedMatch(match);
    setShowResultModal(true);
  };

  const handleResultSuccess = async () => {
    setShowResultModal(false);
    if (selectedTournament) {
      await fetchBracket(selectedTournament.id);
    }
  };

  const handleStartNextRound = async (tournamentId) => {
    try {
      await tournamentService.startNextRound(tournamentId);
      toast.success('دور بعدی با موفقیت شروع شد');
      await fetchBracket(tournamentId);
    } catch (error) {
      toast.error('خطا در شروع دور بعدی');
      console.error('Error starting next round:', error);
    }
  };

  const parsePictureUrl = (pictureString) => {
    try {
      if (pictureString && pictureString.startsWith('{') && pictureString.endsWith('}')) {
        const picObject = JSON.parse(pictureString);
        return picObject.path;
      }
      return pictureString;
    } catch (e) {
      console.error("Failed to parse picture JSON", e);
      return pictureString;
    }
  };

  const formatDate = (dateString) => {
    return formatPersianDate(dateString);
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'Running': return 'در حال انجام';
      case 'Finished': return 'تکمیل شده';
      case 'Pending': return 'در انتظار';
      default: return 'نامشخص';
    }
  };

  if (loading) {
    return (
      <TournamentsContainer>
        <LoadingSpinner>در حال بارگذاری تورنومنت‌ها...</LoadingSpinner>
      </TournamentsContainer>
    );
  }

  return (
    <TournamentsContainer>
      <Header>
        <Title>مدیریت تورنومنت‌ها</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو در تورنومنت‌ها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddTournament} icon={<Plus size={16} />}>
            افزودن تورنومنت
          </Button>
        </SearchContainer>
      </Header>

      {filteredTournaments.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏆</EmptyIcon>
          <EmptyTitle>هیچ تورنومنتی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز تورنومنتی ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <TournamentsGrid>
          {filteredTournaments.map((tournament, index) => (
            <TournamentCard
              key={tournament.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <TournamentImage>
                {tournament.picture ? (
                  <img src={getFullImageUrl(parsePictureUrl(tournament.picture))} alt={tournament.name} />
                ) : (
                  '🏆'
                )}
              </TournamentImage>
              
              <TournamentContent>
                <TournamentHeader>
                  <TournamentInfo>
                    <TournamentName>{tournament.name || 'تورنومنت بدون نام'}</TournamentName>
                    <TournamentId>ID: #{tournament.id}</TournamentId>
                    <TournamentStatus status={tournament.status}>
                      {getStatusText(tournament.status)}
                    </TournamentStatus>
                  </TournamentInfo>
                </TournamentHeader>
                
                <TournamentDetails>
                  <DetailItem>
                    <Calendar size={16} />
                    شروع: {new Date(tournament.startDate).toLocaleDateString('fa-IR')}
                  </DetailItem>
                  <DetailItem>
                    <Calendar size={16} />
                    مهلت: {new Date(tournament.reservationDeadline).toLocaleDateString('fa-IR')}
                  </DetailItem>
                  <DetailItem>
                    <Users size={16} />
                    تیم‌ها: {tournament.teamsCount || 0}
                  </DetailItem>
                  <DetailItem>
                    <DollarSign size={16} />
                    ورودی: {tournament.entryCost || 0} تومان
                  </DetailItem>
                </TournamentDetails>
                
                <TournamentActions>
                  {tournament.status === 'Pending' && (
                    <ActionButton onClick={() => handleStartTournament(tournament)} title="شروع تورنومنت">
                      <Play size={16} />
                    </ActionButton>
                  )}
                  {tournament.status === 'Running' && (
                    <>
                      <ActionButton onClick={() => handleAddWinner(tournament)} title="افزودن برنده">
                        <Trophy size={16} />
                      </ActionButton>
                      <ActionButton onClick={() => handleFinishTournament(tournament)} title="پایان تورنومنت">
                        <Square size={16} />
                      </ActionButton>
                    </>
                  )}
                  <ActionButton onClick={() => handleViewBracket(tournament)} title="مشاهده براکت">
                    <Users size={16} />
                  </ActionButton>
                  <ActionButton onClick={() => handleEditTournament(tournament)} title="ویرایش">
                    <Edit size={16} />
                  </ActionButton>
                </TournamentActions>
              </TournamentContent>
            </TournamentCard>
          ))}
        </TournamentsGrid>
      )}

      {/* Tournament Modal */}
      <TournamentModal
        show={showTournamentModal}
        tournament={selectedTournament}
        onClose={handleTournamentModalClose}
        onSuccess={handleTournamentModalSuccess}
      />

      {/* Tournament Detail Modal */}
      <TournamentDetailModal
        show={showDetailModal}
        tournament={selectedTournament}
        onClose={() => setShowDetailModal(false)}
        onViewBracket={handleViewBracket}
        onEditTournament={handleEditTournament}
      />

      {/* Match Result Modal */}
      <RecordResultModal
        show={showResultModal}
        match={selectedMatch}
        onClose={() => setShowResultModal(false)}
        onSuccess={handleResultSuccess}
      />

      {/* Finish Tournament Modal */}
      <FinishTournamentModal
        show={showAddWinnerModal}
        tournament={selectedTournament}
        onClose={() => setShowAddWinnerModal(false)}
        onFinish={handleFinishTournament}
      />

      {/* Bracket Modal */}
      {showBracketModal && (
        <BracketModal onClick={() => setShowBracketModal(false)}>
          <BracketContent onClick={(e) => e.stopPropagation()}>
            <BracketHeader>
              <BracketTitle>
                <Trophy size={24} />
                {selectedTournament?.name} - براکت
              </BracketTitle>
              <CloseButton onClick={() => setShowBracketModal(false)}>
                <X size={20} />
              </CloseButton>
            </BracketHeader>
            <BracketBody>
              {bracketLoading ? (
                <LoadingSpinner>در حال بارگذاری براکت...</LoadingSpinner>
              ) : (
                <TournamentBracket
                  bracketData={bracketData}
                  isLoading={bracketLoading}
                  onRecordResult={handleRecordResult}
                  onStartNextRound={() => handleStartNextRound(selectedTournament.id)}
                />
              )}
            </BracketBody>
          </BracketContent>
        </BracketModal>
      )}
    </TournamentsContainer>
  );
};

export default Tournaments;